/**
 * Entry point for the Western Express dispatcher prototype.
 *
 * Self-contained: brings its own MUI ThemeProvider so it renders outside the
 * authenticated app shell and can be opened directly at `/western-express`
 * with no login or workspace bootstrap. Top-level nav switches between the two
 * designs — the Task Feed and the Freight Status board.
 */

import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Snackbar from "@mui/material/Snackbar";
import { ThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useState } from "react";

import theme from "@/theme/theme";
import { buildDriverActivity, buildDriverThread, FEED_DRIVER_BY_ID, type FeedMessage } from "./boards";
import { accent, text, zinc } from "./colors";
import { DriverMessageView } from "./DriverMessageView";
import { DRIVERS, TASKS } from "./data";
import { FreightStatusView } from "./FreightStatusView";
import { LiveMessageFeedView } from "./LiveMessageFeedView";
import { TaskFeedView } from "./TaskFeedView";
import type { DriverActivityEvent, TaskState } from "./types";

type View = "tasks" | "feed" | "drivers";

interface OpenedDriver {
  id: string;
  name: string;
  truck: string;
  messages: FeedMessage[];
  activity: DriverActivityEvent[];
}

function NavButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 14,
        fontWeight: 700,
        color: active ? text.primary : text.muted,
        bgcolor: active ? zinc[800] : "transparent",
        border: `1px solid ${active ? zinc[700] : "transparent"}`,
        borderRadius: "8px",
        px: 1.75,
        py: 0.875,
        transition: "all 120ms ease",
        "&:hover": { color: text.primary, bgcolor: zinc[800] },
      }}
    >
      {label}
    </Box>
  );
}

export default function WesternExpressPrototype() {
  const [view, setView] = useState<View>("tasks");
  const [toast, setToast] = useState<string | null>(null);
  // Shared task-resolution state so the live feed's board un-addressed counts
  // react when a dispatcher resolves a task from the Task Feed.
  const [resolved, setResolved] = useState<Record<string, TaskState>>({});
  const [openedDriver, setOpenedDriver] = useState<OpenedDriver | null>(null);

  const openDriverChannel = (driverId: string) => {
    const feedDriver = FEED_DRIVER_BY_ID.get(driverId);
    const detailed = DRIVERS.find((d) => d.id === driverId);
    const name = feedDriver?.name ?? detailed?.name ?? "Driver";
    const truck = feedDriver?.truck ?? detailed?.truck ?? "—";
    setOpenedDriver({
      id: driverId,
      name,
      truck,
      messages: buildDriverThread(driverId),
      activity: buildDriverActivity(driverId),
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: zinc[950],
          color: text.primary,
        }}
      >
        {/* top bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${zinc[800]}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mr: 1 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                bgcolor: accent.purple,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocalShippingIcon sx={{ fontSize: 18, color: "#fff" }} />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: text.primary }}>
              Western Express{" "}
              <Box component="span" sx={{ color: text.muted, fontWeight: 600 }}>
                · Fleet
              </Box>
            </Typography>
            <Box sx={{ ml: 1, px: 1, py: 0.25, borderRadius: "6px", border: `1px solid ${zinc[700]}` }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: text.muted, letterSpacing: 0.5 }}>
                PROTOTYPE
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <NavButton label="Tasks" active={view === "tasks"} onClick={() => setView("tasks")} />
            <NavButton label="Live Feed" active={view === "feed"} onClick={() => setView("feed")} />
            <NavButton label="Freight Status" active={view === "drivers"} onClick={() => setView("drivers")} />
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.875,
              minWidth: 260,
              borderRadius: "8px",
              border: `1px solid ${zinc[700]}`,
              bgcolor: zinc[900],
              color: text.muted,
            }}
          >
            <SearchIcon sx={{ fontSize: 17 }} />
            <Typography sx={{ fontSize: 13 }}>Search drivers…</Typography>
          </Box>
        </Box>

        {/* body */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {openedDriver ? (
            <DriverMessageView
              key={openedDriver.id}
              driverName={openedDriver.name}
              truck={openedDriver.truck}
              messages={openedDriver.messages}
              activity={openedDriver.activity}
              onBack={() => setOpenedDriver(null)}
            />
          ) : view === "tasks" ? (
            <TaskFeedView
              drivers={DRIVERS}
              tasks={TASKS}
              onToast={setToast}
              resolved={resolved}
              onResolveTask={(taskId, nextState) => setResolved((prev) => ({ ...prev, [taskId]: nextState }))}
              onOpenMessageHistory={(driver) => openDriverChannel(driver.id)}
            />
          ) : view === "feed" ? (
            <LiveMessageFeedView resolved={resolved} onToast={setToast} />
          ) : (
            <FreightStatusView />
          )}
        </Box>
      </Box>

      <Snackbar
        open={toast !== null}
        autoHideDuration={2600}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={toast ?? ""}
      />
    </ThemeProvider>
  );
}
